import { default as Dropdown } from 'antd/es/dropdown';
import type { MenuProps } from 'antd/es/menu';
import { Ellipsis } from 'lucide-react';
import { useState } from 'react';

import type { IntegrationBusinessUnit } from 'api';
import { Restricted } from 'containers/Restricted';

import {
  BUSINESS_UNIT_CARD_ACTIONS_TRIGGER_TEST_ID,
  BUSINESS_UNIT_CARD_DELETE_OPTION_TEST_ID,
  BUSINESS_UNIT_CARD_RENAME_OPTION_TEST_ID,
  BUSINESS_UNIT_CARD_SET_DEFAULT_OPTION_TEST_ID,
  DELETE_DISABLED_REASON,
  DELETE_LABEL,
  RENAME_LABEL,
  SET_DEFAULT_DISABLED_REASON,
  SET_DEFAULT_LABEL
} from './constants';
import { DeleteBusinessUnitModal } from './DeleteBusinessUnitModal';
import { RenameBusinessUnitModal } from './RenameBusinessUnitModal';
import { SetDefaultBusinessUnitModal } from './SetDefaultBusinessUnitModal';

type BusinessUnitCardActionsProps = {
  businessUnit: IntegrationBusinessUnit;
};

type OpenModal = 'rename' | 'set-default' | 'delete' | null;

export const BusinessUnitCardActions = ({
  businessUnit
}: BusinessUnitCardActionsProps) => {
  const [modal, setModal] = useState<OpenModal>(null);
  const closeModal = () => setModal(null);

  const items: MenuProps['items'] = [
    {
      key: 'rename',
      onClick: () => setModal('rename'),
      label: (
        <span
          data-testid={BUSINESS_UNIT_CARD_RENAME_OPTION_TEST_ID(
            businessUnit.uuid
          )}
        >
          {RENAME_LABEL}
        </span>
      )
    },
    {
      key: 'set-default',
      disabled: businessUnit.default,
      onClick: () => setModal('set-default'),
      label: (
        <span
          data-testid={BUSINESS_UNIT_CARD_SET_DEFAULT_OPTION_TEST_ID(
            businessUnit.uuid
          )}
        >
          {SET_DEFAULT_LABEL}
          {businessUnit.default && (
            <span className="block text-xs text-content-secondary">
              {SET_DEFAULT_DISABLED_REASON}
            </span>
          )}
        </span>
      )
    },
    {
      key: 'delete',
      danger: true,
      disabled: businessUnit.default,
      onClick: () => setModal('delete'),
      label: (
        <span
          data-testid={BUSINESS_UNIT_CARD_DELETE_OPTION_TEST_ID(
            businessUnit.uuid
          )}
        >
          {DELETE_LABEL}
          {businessUnit.default && (
            <span className="block text-xs text-content-secondary">
              {DELETE_DISABLED_REASON}
            </span>
          )}
        </span>
      )
    }
  ];

  return (
    <Restricted requiredPermission="configurations:manage">
      <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
        <button
          type="button"
          aria-label="Business unit actions"
          data-testid={BUSINESS_UNIT_CARD_ACTIONS_TRIGGER_TEST_ID(
            businessUnit.uuid
          )}
          className="cursor-pointer appearance-none border-0 bg-transparent p-0 text-content-primary hover:text-content-secondary"
        >
          <Ellipsis className="size-5" />
        </button>
      </Dropdown>
      <RenameBusinessUnitModal
        businessUnit={businessUnit}
        open={modal === 'rename'}
        onClose={closeModal}
      />
      <SetDefaultBusinessUnitModal
        businessUnit={businessUnit}
        open={modal === 'set-default'}
        onCancel={closeModal}
        onConfirmed={closeModal}
      />
      <DeleteBusinessUnitModal
        businessUnit={businessUnit}
        open={modal === 'delete'}
        onCancel={closeModal}
        onConfirmed={closeModal}
      />
    </Restricted>
  );
};
